import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeviceTypeService } from '@services/device-type.service';
import { ServiceWorkerService } from '@services/service-worker.service';
import { UserService } from '@services/user.service';
import { WebsocketService } from '@services/websocket.service';
import { getTranslation } from '@shared/translate-helper';
import { MessageService } from 'primeng/api';
import { DEVICE_MOBILE_MAX_WIDTH, DEVICE_TABLET_MAX_WIDTH } from './constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public checkNoHeaderRoutes = (): boolean => {
    const url = this.route.url;
    // The agent builder (create/edit) takes over the full viewport, so the global header is hidden.
    return url.includes('onboarding/') || url.includes('agents/new') || (url.includes('agents/') && url.includes('/edit'));
  };
  private hiddenAt: number | null = null;
  private readonly RECONNECT_AFTER_HIDDEN_MS = 30_000;

  constructor(
    private readonly route: Router,
    private readonly translate: TranslateService,
    private readonly swService: ServiceWorkerService,
    private readonly userService: UserService,
    private readonly messageService: MessageService,
    public deviceType: DeviceTypeService,
    private ws: WebsocketService,
  ) {}

  /* In the Medullar browser extension, resizing the window does not adapt the UI correctly because we check just the device type
    and not the actual window size as well. So here we listen to resize events and update the device type accordingly. */
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkScreenSize(event);
  }

  private offlineHandler = () => {
    getTranslation(this.translate, ['connection_lost', 'connection_lost_description']).subscribe((res: any) => {
      this.messageService.add({
        severity: 'warn',
        summary: res['connection_lost'],
        detail: res['connection_lost_description'],
        sticky: true,
      });
    });
  };

  private onlineHandler = () => {
    this.messageService.clear();
    getTranslation(this.translate, ['connection_restored', 'connection_restored_description']).subscribe((res: any) => {
      this.messageService.add({
        severity: 'success',
        summary: res['connection_restored'],
        detail: res['connection_restored_description'],
        life: 3000,
      });
    });

    // Force a fresh WebSocket — the SDK's status signal can lie after the OS
    // network flapped (zombie connection), so don't gate on webSocketStatus.
    if (this.userService.getUserData()?.uuid) {
      this.ws.forceReconnect();
    }
  };

  private visibilityHandler = () => {
    if (document.hidden) {
      this.hiddenAt = Date.now();
      return;
    }

    const hiddenAt = this.hiddenAt;
    this.hiddenAt = null;

    if (hiddenAt === null || Date.now() - hiddenAt < this.RECONNECT_AFTER_HIDDEN_MS) return;
    if (!this.userService.getUserData()?.uuid) return;

    this.ws.forceReconnect();
  };

  ngOnInit(): void {
    this.swService.checkForUpdates();
    this.swService.handleUnrecoverableState();
    window.addEventListener('offline', this.offlineHandler);
    window.addEventListener('online', this.onlineHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.checkScreenSize();
  }

  checkScreenSize(event?: Event): void {
    const width = event?.target instanceof Window ? event.target.innerWidth : window.innerWidth;
    this.deviceType.isMobile = false;
    this.deviceType.isTablet = false;
    this.deviceType.isDesktop = false;

    if (width < DEVICE_MOBILE_MAX_WIDTH) {
      this.deviceType.isMobile = true;
    } else if (width >= DEVICE_MOBILE_MAX_WIDTH && width < DEVICE_TABLET_MAX_WIDTH) {
      this.deviceType.isTablet = true;
    } else {
      this.deviceType.isDesktop = true;
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('offline', this.offlineHandler);
    window.removeEventListener('online', this.onlineHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.ws.closeWebSocket();
  }
}
